<template>
  <figure class="highcharts-figure">
    <highcharts ref="chart" :options="options" />
    <p class="highcharts-description highcharts-linked-description">
      <strong>Figure : </strong>
      {{ $t('compGraphicsPlanesCumulativeEmissions.help') }}
    </p>
  </figure>
</template>

<script>
import '@/plugins/highcharts.js'
import Travel from '@/models/carbon/Travel.js'

export default {
  name: 'PlanesCumulativeEmissions',
  props: {
    ghgi: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      options: {}
    }
  },
  methods: {
    compute () {
      let self = this
      let max = -Infinity
      let size = 1000
      for (let travel of this.ghgi.travels) {
        for (let section of travel.sections) {
          if (section.transportation === Travel.MODE_PLANE) {
            // we only consider one way distance of sections
            // round trip has to be correctly handled
            // see below
            let distance = section.getOneWayCorrectedDistance()
            // separate the two cases to allow tests with a single travel
            if (distance > max) {
              max = distance
            }
          }
        }
      }
      let bins = Math.ceil(max / size)
      let histogram = new Array(bins).fill(0)
      let hintensities = new Array(bins).fill(0)
      let totalintensity = 0
      for (let travel of this.ghgi.travels) {
        for (let section of travel.sections) {
          if (section.transportation === Travel.MODE_PLANE) {
            // NOTE(msimonin): it's relevant to use the one way distance of the section
            // since a round trip section is accounted for 2 here
            let distance = section.getOneWayCorrectedDistance()
            let intensity = section
              .getCarbonIntensity(this.ghgi.year)
              .multiply(travel.amount)
            let bin = Math.floor(distance / size)
            let sectionMultiplier = section.isRoundTrip ? 2 : 1
            histogram[bin] += travel.amount * sectionMultiplier
            hintensities[bin] += intensity.intensity
            totalintensity += intensity.intensity
          }
        }
      }
      let categories = []
      let binCenter = size / 2
      for (let i in histogram) {
        categories[i] = binCenter
        binCenter += size
      }
      let cumSum = 0
      for (let index in hintensities) {
        cumSum += hintensities[index]
        hintensities[index] = cumSum
      }
      hintensities = hintensities.map((val) => (val / totalintensity) * 100)
      hintensities[hintensities.length - 1] = 100
      this.options = {
        chart: {
          type: 'column'
        },
        title: {
          text: ''
        },
        xAxis: {
          categories: categories,
          crosshair: true,
          title: {
            text: self.$t('compGraphicsPlanesCumulativeEmissions.label-4')
          }
        },
        yAxis: [
          {
            gridLineWidth: 0,
            title: {
              text: self.$t('compGraphicsPlanesCumulativeEmissions.label-2')
            }
          },
          {
            max: 100,
            labels: {
              format: '{value}%'
            },
            title: {
              text:
                self.$t('compGraphicsPlanesCumulativeEmissions.label-1') +
                ' (%)'
            },
            opposite: true
          }
        ],
        tooltip: {
          formatter: function () {
            let inf = this.points[0].x - size / 2
            let sup = this.points[0].x + size / 2
            let tooltip = inf + ' km - ' + sup + ' km <br/>'
            tooltip +=
              '<span style="color:' +
              this.points[0].series.color +
              ';">\u25CF ' +
              '</span> '
            tooltip +=
              this.points[0].series.name +
              ': <b>' +
              this.points[0].y +
              ' </b><br/>'
            tooltip +=
              '<span style="color:' +
              this.points[1].series.color +
              ';">\u25CF ' +
              '</span> '
            tooltip +=
              this.points[1].series.name +
              ': <b>' +
              Math.round(this.points[1].y * 100) / 100 +
              ' %</b>'
            return tooltip
          },
          shared: true
        },
        plotOptions: {
          series: {
            groupPadding: 0,
            pointPadding: 0
          }
        },
        series: [
          {
            name: self.$t('compGraphicsPlanesCumulativeEmissions.label-2'),
            type: 'column',
            data: histogram,
            tooltip: {
              valueSuffix:
                ' ' + self.$t('compGraphicsPlanesCumulativeEmissions.label-3')
            }
          },
          {
            name: self.$t('compGraphicsPlanesCumulativeEmissions.label-1'),
            type: 'spline',
            data: hintensities,
            yAxis: 1,
            tooltip: {
              valueSuffix: ' %'
            },
            color: '#FF0000'
          }
        ],
        legend: {
          enabled: false
        }
      }
    }
  },
  mounted () {
    let self = this
    this.compute()
    setTimeout(function () {
      self.$refs.chart.chart.redraw()
    }, 1)
  }
}
</script>

<style scoped>
.highcharts-figure {
  min-width: 320px;
  max-width: 700px;
  margin: 1em auto;
}
.highcharts-description {
  text-align: center;
  color: '#777777';
  font-style: italic;
}
</style>
